import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  query,
  orderBy,
  getDocs,
  serverTimestamp,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuthStore } from './auth'
import { useTeamsStore } from './teams'
import { writeAuditLog } from './auditLog'
import { TeamType, TeamStatus } from '@/types'
import type { OperationalPeriod } from '@/types'

export const useOpsStore = defineStore('ops', () => {
  const ops = ref<OperationalPeriod[]>([])
  const selectedOpId = ref<string | null>(null)

  const selectedOp = computed(
    () => ops.value.find((o) => o.op_id === selectedOpId.value) ?? null,
  )

  let unsubscribe: Unsubscribe | null = null
  let _incidentId = ''

  function subscribeToOps(incidentId: string) {
    if (incidentId === _incidentId && unsubscribe) return
    if (unsubscribe) unsubscribe()
    _incidentId = incidentId

    unsubscribe = onSnapshot(
      query(
        collection(db, 'incidents', incidentId, 'operational_periods'),
        orderBy('op_number', 'asc'),
      ),
      (snap) => {
        ops.value = snap.docs.map((d) => ({ op_id: d.id, ...d.data() }) as OperationalPeriod)

        // Auto-select latest OP if nothing is selected or current selection no longer exists
        const stillValid = ops.value.some((o) => o.op_id === selectedOpId.value)
        if (!stillValid) {
          selectedOpId.value = ops.value.length > 0 ? ops.value[ops.value.length - 1]!.op_id : null
        }
      },
      () => { /* permission-denied or offline — leave selectedOpId as-is */ },
    )
  }

  function selectOp(opId: string) {
    selectedOpId.value = opId
  }

  async function createOp(incidentId: string, data: { date: string }) {
    const authStore = useAuthStore()

    // Always compute op_number from the collection — never accept it from caller
    const snap = await getDocs(
      query(
        collection(db, 'incidents', incidentId, 'operational_periods'),
        orderBy('op_number', 'asc'),
      ),
    )
    const maxOpNumber = snap.docs.reduce((max, d) => {
      const n = (d.data().op_number as number) || 0
      return n > max ? n : max
    }, 0)
    const newOpNumber = maxOpNumber + 1

    const docRef = await addDoc(
      collection(db, 'incidents', incidentId, 'operational_periods'),
      {
        op_number: newOpNumber,
        date: data.date,
        created_by: authStore.currentUser!.uid,
        created_at: serverTimestamp(),
      },
    )

    await writeAuditLog(incidentId, 'op.created', 'op', docRef.id, null, {
      op_number: newOpNumber,
    })

    // Copy comms_plan forward from prior OP (if one exists)
    if (snap.docs.length > 0) {
      const priorOpId = snap.docs[snap.docs.length - 1]!.id
      const priorChannels = await getDocs(
        collection(db, 'incidents', incidentId, 'operational_periods', priorOpId, 'comms_plan'),
      )
      if (!priorChannels.empty) {
        const batch = writeBatch(db)
        priorChannels.docs.forEach((ch) => {
          const newRef = doc(collection(db, 'incidents', incidentId, 'operational_periods', docRef.id, 'comms_plan'))
          batch.set(newRef, { ...ch.data(), created_at: serverTimestamp() })
        })
        await batch.commit()
      }
    }

    // Auto-create Overhead team for every new OP — starts In Assignment per checkin-flow spec
    const teamsStore = useTeamsStore()
    await teamsStore.createTeam(incidentId, docRef.id, {
      team_identifier: `${newOpNumber}OH`,
      team_type: TeamType.Overhead,
      team_status: TeamStatus.OnScene,
    })

    return docRef.id
  }

  async function updateOpCaltopoLink(
    incidentId: string,
    opId: string,
    mapId: string | null,
    mapName: string | null,
    caltopoOpId: string | null,
    pollIntervalMs: number | null = null,
  ) {
    await updateDoc(doc(db, 'incidents', incidentId, 'operational_periods', opId), {
      caltopo_map_id: mapId,
      caltopo_map_name: mapName,
      caltopo_op_id: caltopoOpId,
      caltopo_poll_interval_ms: pollIntervalMs,
    })
  }

  async function updateOp(
    incidentId: string,
    opId: string,
    updates: Partial<OperationalPeriod>,
  ) {
    const before = ops.value.find((o) => o.op_id === opId) ?? null
    await updateDoc(doc(db, 'incidents', incidentId, 'operational_periods', opId), updates)
    await writeAuditLog(
      incidentId,
      'op.updated',
      'op',
      opId,
      before as Record<string, unknown> | null,
      updates as Record<string, unknown>,
    )
  }

  function teardown() {
    if (unsubscribe) {
      unsubscribe()
      unsubscribe = null
    }
    _incidentId = ''
    ops.value = []
    selectedOpId.value = null
  }

  return {
    ops,
    selectedOpId,
    selectedOp,
    subscribeToOps,
    selectOp,
    createOp,
    updateOpCaltopoLink,
    updateOp,
    teardown,
  }
})
